import type { ComponentType, SVGProps } from 'react';
import {
  IconBeacon,
  IconBrand,
  IconCampaigns,
  IconHome,
  IconIntelligence,
  IconIntegrations,
  IconProfile,
  IconPublication,
  IconQueue,
  IconScheduler,
  IconSettings,
  IconSources,
  IconStudio,
  IconTone,
  IconTracker,
} from './icons';

export type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string }>;

export type NavAvailability = 'ready' | 'planned';

export interface DockDestination {
  id: string;
  to: string;
  label: string;
  shortLabel: string;
  icon: IconComponent;
  availability: NavAvailability;
  /** Match when path equals or starts with these prefixes (except home). */
  matchPrefixes?: string[];
}

/** Contextual (left-rail flyout) destination within a primary workspace. */
export interface ContextualNavItem {
  id: string;
  to: string;
  label: string;
  icon: IconComponent;
  availability: NavAvailability;
  /** Optional exact-path or prefix match overrides for active state. */
  matchPrefixes?: string[];
  /** When true, only exact path matches (not nested children). */
  exactMatch?: boolean;
}

/**
 * Desktop Adaptive Command Dock — Stage 18C / 19H / 19I primary destinations (exactly 8).
 * Stage 19H consolidated Tracker + Beacon into Intelligence.
 * Stage 19I removed Publications as a primary destination — publication identity
 * switching lives on Home, and editorial config (`/publication`, `/tone-registers`,
 * `/brand`) is reachable directly without a dedicated dock item. Integrations
 * (content sources, publishing, AI providers) is now a Settings section.
 * Order is product IA: do not reorder without an explicit navigation stage.
 */
export const DESKTOP_DOCK_ITEMS: DockDestination[] = [
  {
    id: 'home',
    to: '/',
    label: 'Home',
    shortLabel: 'Home',
    icon: IconHome,
    availability: 'ready',
  },
  {
    id: 'sources',
    to: '/sources',
    label: 'Sources',
    shortLabel: 'Sources',
    icon: IconSources,
    availability: 'ready',
    matchPrefixes: ['/sources'],
  },
  {
    id: 'campaigns',
    to: '/campaigns',
    label: 'Campaigns',
    shortLabel: 'Campaigns',
    icon: IconCampaigns,
    availability: 'ready',
    matchPrefixes: ['/campaigns'],
  },
  {
    id: 'studio',
    to: '/studio',
    label: 'Studio',
    shortLabel: 'Studio',
    icon: IconStudio,
    availability: 'ready',
    matchPrefixes: ['/studio'],
  },
  {
    id: 'scheduler',
    to: '/scheduler',
    label: 'Scheduler',
    shortLabel: 'Scheduler',
    icon: IconScheduler,
    availability: 'ready',
    matchPrefixes: ['/scheduler'],
  },
  {
    id: 'queue',
    to: '/queue',
    label: 'Queue',
    shortLabel: 'Queue',
    icon: IconQueue,
    availability: 'ready',
    matchPrefixes: ['/queue'],
  },
  {
    id: 'intelligence',
    to: '/intelligence',
    label: 'Intelligence',
    shortLabel: 'Intel',
    icon: IconIntelligence,
    availability: 'ready',
    matchPrefixes: ['/intelligence', '/tracker', '/beacon'],
  },
  {
    id: 'settings',
    to: '/settings',
    label: 'Settings',
    shortLabel: 'Settings',
    icon: IconSettings,
    availability: 'ready',
    // Legacy Publications editorial routes keep Settings selected — Stage 19I
    // folded Publications out of the primary dock but `/publication`,
    // `/tone-registers`, and `/brand` still work as non-primary deep links.
    matchPrefixes: [
      '/settings',
      '/integrations',
      '/system',
      '/publications',
      '/publication',
      '/tone-registers',
      '/brand',
    ],
  },
];

/**
 * Mobile bottom navigation — same primary destinations as desktop.
 * Narrow widths use horizontal scroll rather than a More overflow menu.
 */
export const MOBILE_NAV_ITEMS: DockDestination[] = DESKTOP_DOCK_ITEMS;

/** Intelligence workspace contextual navigation (Stage 19H). */
export const INTELLIGENCE_CONTEXT_NAV: ContextualNavItem[] = [
  {
    id: 'intelligence-overview',
    to: '/intelligence',
    label: 'Intelligence',
    icon: IconIntelligence,
    availability: 'ready',
    matchPrefixes: ['/intelligence'],
    exactMatch: true,
  },
  {
    id: 'intelligence-tracker',
    to: '/intelligence/tracker',
    label: 'Tracker',
    icon: IconTracker,
    availability: 'ready',
    matchPrefixes: ['/intelligence/tracker', '/tracker'],
  },
  {
    id: 'intelligence-beacon',
    to: '/intelligence/beacon',
    label: 'Beacon',
    icon: IconBeacon,
    availability: 'ready',
    matchPrefixes: ['/intelligence/beacon', '/beacon'],
  },
];

/** Publications workspace contextual navigation (Stage 18C). */
export const PUBLICATIONS_CONTEXT_NAV: ContextualNavItem[] = [
  {
    id: 'publications-list',
    to: '/publications',
    label: 'Publications',
    icon: IconPublication,
    availability: 'ready',
    matchPrefixes: ['/publications'],
  },
  {
    id: 'editorial-profile',
    to: '/publication',
    label: 'Editorial Profile',
    icon: IconProfile,
    availability: 'ready',
    matchPrefixes: ['/publication'],
  },
  {
    id: 'tone-registers',
    to: '/tone-registers',
    label: 'Tone Registers',
    icon: IconTone,
    availability: 'ready',
    matchPrefixes: ['/tone-registers'],
  },
  {
    id: 'brand',
    to: '/brand',
    label: 'Brand',
    icon: IconBrand,
    availability: 'planned',
    matchPrefixes: ['/brand'],
  },
];

/**
 * Settings workspace contextual navigation (Stage 19I Integrations Control Centre).
 * Exactly six sections. Profile edits live under Account (`/settings/profile` link
 * inside the Account screen); System / Jobs and legacy Publications routes still
 * work as direct deep links but are no longer listed here as primary sections.
 */
export const SETTINGS_CONTEXT_NAV: ContextualNavItem[] = [
  {
    id: 'account',
    to: '/settings/account',
    label: 'Account',
    icon: IconProfile,
    availability: 'ready',
  },
  {
    id: 'organisation',
    to: '/settings/organisation',
    label: 'Organisation',
    icon: IconSettings,
    availability: 'ready',
  },
  {
    id: 'integrations',
    to: '/settings/integrations',
    label: 'Integrations',
    icon: IconIntegrations,
    availability: 'ready',
    matchPrefixes: ['/settings/integrations', '/integrations'],
  },
  {
    id: 'team',
    to: '/settings/team',
    label: 'Team',
    icon: IconSettings,
    availability: 'ready',
  },
  {
    id: 'security',
    to: '/settings/security',
    label: 'Security',
    icon: IconSettings,
    availability: 'ready',
  },
  {
    id: 'preferences',
    to: '/settings/preferences',
    label: 'Preferences',
    icon: IconSettings,
    availability: 'ready',
  },
];

/** @deprecated Prefer DESKTOP_DOCK_ITEMS / MOBILE_NAV_ITEMS. Kept for migration clarity. */
export const NAV_ITEMS = DESKTOP_DOCK_ITEMS.map((item) => ({
  to: item.to,
  label: item.label,
  shortLabel: item.shortLabel,
}));

export function isMobileNavWidth(width: number): boolean {
  return width <= 800;
}

export function isNarrowDesktopWidth(width: number): boolean {
  return width > 800 && width <= 1100;
}

export function isDockDestinationActive(pathname: string, item: DockDestination): boolean {
  if (item.to === '/') {
    return pathname === '/';
  }
  const prefixes = item.matchPrefixes ?? [item.to];
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function isContextualNavItemActive(pathname: string, item: ContextualNavItem): boolean {
  const prefixes = item.matchPrefixes ?? [item.to];
  return prefixes.some((prefix) => {
    if (item.exactMatch) {
      return pathname === prefix;
    }
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}

/** Resolve which primary dock destination owns the current path (if any). */
export function getActiveDockDestination(pathname: string): DockDestination | undefined {
  // Prefer the most specific non-home match when prefixes could overlap (they should not).
  return DESKTOP_DOCK_ITEMS.find((item) => isDockDestinationActive(pathname, item));
}
